import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/AuthContext'
import {
  canManageStateRegulatory,
  stageStateImport,
  importStateRegulatory,
  type StateStageSummary,
  type StateImportResult,
} from '../../../lib/services/stateRegulatory'

type WizardStep = 'upload' | 'preview' | 'result'

export function StateImportWizard() {
  const { viewerContext } = useAuth()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<WizardStep>('upload')
  const [rawJson, setRawJson] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [stageSummary, setStageSummary] = useState<StateStageSummary | null>(null)
  const [importResult, setImportResult] = useState<StateImportResult | null>(null)

  const stageMutation = useMutation({
    mutationFn: stageStateImport,
    onSuccess: (data) => {
      setStageSummary(data)
      setStep('preview')
    },
  })

  const importMutation = useMutation({
    mutationFn: importStateRegulatory,
    onSuccess: async (data) => {
      setImportResult(data)
      setStep('result')
      await queryClient.invalidateQueries({ queryKey: ['state-regulatory-bodies'] })
    },
  })

  if (!canManageStateRegulatory(viewerContext)) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        State regulatory import is restricted to superadmin role.
      </div>
    )
  }

  function handleStage() {
    setParseError(null)
    try {
      const payload = JSON.parse(rawJson)
      stageMutation.mutate(payload)
    } catch {
      setParseError('Invalid JSON. Please check the payload format.')
    }
  }

  function handleImport() {
    try {
      const payload = JSON.parse(rawJson)
      importMutation.mutate(payload)
    } catch {
      setParseError('Invalid JSON.')
    }
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setRawJson(ev.target?.result as string)
      setParseError(null)
    }
    reader.readAsText(file)
  }

  function reset() {
    setStep('upload')
    setRawJson('')
    setParseError(null)
    setStageSummary(null)
    setImportResult(null)
    stageMutation.reset()
    importMutation.reset()
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">
          Import State Regulatory Pack
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload a state regulatory import payload JSON to validate and import.
        </p>
      </header>

      {step === 'upload' && (
        <section className="v11-section space-y-4 p-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Upload JSON file
            </label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:rounded file:border-0 file:bg-[var(--brand-primary)] file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:opacity-90"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Or paste JSON payload
            </label>
            <textarea
              className="h-64 w-full rounded border border-gray-300 p-3 font-mono text-xs"
              value={rawJson}
              onChange={(e) => setRawJson(e.target.value)}
              placeholder='{"state_code": "FL", "agency": {...}, "controls": [...], "crosswalk_mappings": [...]}'
            />
          </div>

          {parseError && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {parseError}
            </div>
          )}

          {stageMutation.isError && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              Staging failed: {(stageMutation.error as Error).message}
            </div>
          )}

          <button
            className="btn-primary"
            onClick={handleStage}
            disabled={!rawJson.trim() || stageMutation.isPending}
          >
            {stageMutation.isPending ? 'Validating...' : 'Validate (Stage)'}
          </button>
        </section>
      )}

      {step === 'preview' && stageSummary && (
        <section className="v11-section space-y-4 p-5">
          <h2 className="text-lg font-bold text-green-700">Validation Passed</h2>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="v11-mini-metric">
              <div className="text-lg font-bold">{stageSummary.state_code}</div>
              <div className="text-xs text-gray-500">State Code</div>
            </div>
            <div className="v11-mini-metric">
              <div className="text-lg font-bold">{stageSummary.counts.controls}</div>
              <div className="text-xs text-gray-500">Controls</div>
            </div>
            <div className="v11-mini-metric">
              <div className="text-lg font-bold">{stageSummary.counts.crosswalk_mappings}</div>
              <div className="text-xs text-gray-500">Crosswalk Mappings</div>
            </div>
            <div className="v11-mini-metric">
              <div className="truncate text-xs font-mono">{stageSummary.deterministic_signature.slice(0, 16)}...</div>
              <div className="text-xs text-gray-500">Signature</div>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            Agency: <strong>{stageSummary.agency_name}</strong>
          </p>

          {importMutation.isError && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              Import failed: {(importMutation.error as Error).message}
            </div>
          )}

          <div className="flex gap-3">
            <button
              className="btn-primary"
              onClick={handleImport}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Importing...' : 'Confirm Import'}
            </button>
            <button className="btn-ghost" onClick={reset}>
              Cancel
            </button>
          </div>
        </section>
      )}

      {step === 'result' && importResult && (
        <section className="v11-section space-y-4 p-5">
          <h2 className="text-lg font-bold text-green-700">
            Import Successful
          </h2>
          <p className="text-sm text-gray-600">
            State <strong>{importResult.state_code}</strong> ({importResult.agency_name}) imported successfully.
          </p>
          {importResult.counts && (
            <p className="text-sm text-gray-500">
              {importResult.counts.controls_upserted} controls and{' '}
              {importResult.counts.crosswalk_mappings_upserted} crosswalk mappings upserted.
            </p>
          )}
          <button className="btn-primary" onClick={reset}>
            Import Another State
          </button>
        </section>
      )}
    </div>
  )
}
